'use client';

/**
 * OrgUsageChart
 *
 * Multi-user time-series chart for the Admin → Usage tab.
 * Features:
 *   - Metric toggle: Messages / Sessions / Files
 *   - Range toggle: 7d / 30d / 90d (drives useAdminUsageTimeseriesQuery)
 *   - Per-user legend with toggle (click to hide/show individual series)
 *   - Stacked AreaChart from recharts
 *   - Loading skeleton and empty state
 */

import * as React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, ChevronLeft, ChevronRight, FileText, MessageSquare, TrendingUp } from 'lucide-react';
import { useAdminTeamMembersQuery, useAdminUsageTimeseriesQuery } from '../hooks/use-gateway-queries';
import { cn } from '../utils/cn';
import {
  cartesianGridProps,
  getChartColors,
  getUserColor,
  tooltipContainerStyle,
} from '../utils/chart-theme';
import {
  formatBucketRangeLabel,
  seriesForChartRange,
  type MetricKey,
} from '../utils/chart-series';

// ─── Types ────────────────────────────────────────────────────────────────────

type TimeseriesUser = {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
};

const METRIC_OPTIONS: { key: MetricKey; label: string; icon: React.ReactNode }[] = [
  { key: 'messages', label: 'Messages', icon: <MessageSquare size={12} /> },
  { key: 'sessions', label: 'Sessions', icon: <Activity size={12} /> },
  { key: 'files', label: 'Files', icon: <FileText size={12} /> },
];

type RangeKey = '7d' | '30d' | '90d';

/** windowDays = raw daily points fetched; stepDays = arrow navigation stride */
const RANGE_OPTIONS: { key: RangeKey; label: string; windowDays: number; stepDays: number }[] = [
  { key: '7d', label: '7d', windowDays: 7, stepDays: 7 },
  { key: '30d', label: '30d', windowDays: 30, stepDays: 30 }, // 10 × 3-day triplets
  { key: '90d', label: '90d', windowDays: 90, stepDays: 90 }, // chart: 12 × 7-day weekly averages
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function displayName(u: TimeseriesUser): string {
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
  if (u.firstName) return u.firstName;
  const localPart = u.email.split('@')[0];
  return localPart || u.email || 'User';
}

function shortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftDateKey(iso: string, deltaDays: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function buildDateRange(end: string, days: number): string[] {
  return Array.from({ length: days }, (_, i) => shiftDateKey(end, -(days - 1 - i)));
}

function formatPeriodLabel(start: string, end: string): string {
  try {
    const startDate = new Date(`${start}T00:00:00.000Z`);
    const endDate = new Date(`${end}T00:00:00.000Z`);
    const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
    const fmt = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
    const endFmt = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${fmt.format(startDate)} – ${endFmt.format(endDate)}`;
  } catch {
    return `${start} – ${end}`;
  }
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const ChartSkeleton = () => (
  <div className="space-y-3">
    <div className="flex gap-4 border-b border-border pb-2">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-8 w-24 rounded-md bg-muted/40 animate-pulse" />
      ))}
    </div>
    <div className="flex items-center justify-between gap-4">
      <div className="flex gap-1.5">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-7 w-10 rounded-xl bg-muted/40 animate-pulse" />
        ))}
      </div>
      <div className="h-7 w-40 rounded-lg bg-muted/40 animate-pulse" />
    </div>
    <div className="h-48 rounded-xl bg-muted/30 animate-pulse" />
    <div className="flex flex-wrap gap-2">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-5 w-24 rounded-full bg-muted/30 animate-pulse" />
      ))}
    </div>
  </div>
);

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const CustomTooltip = ({
  active,
  payload,
  label,
  users,
  colorMap,
  metric,
  isAggregated,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
  users: TimeseriesUser[];
  colorMap: Map<string, string>;
  metric: MetricKey;
  isAggregated?: boolean;
}) => {
  if (!active || !payload?.length) return null;
  const userMap = new Map(users.map(u => [u.userId, u]));
  const total = payload.reduce((sum, p) => sum + (p.value ?? 0), 0);
  const row = payload[0]?.payload as
    | { dateLabel?: string; dateStart?: string; dateEnd?: string; bucketSize?: number }
    | undefined;
  const heading =
    row?.dateLabel ??
    (row?.dateStart && row?.dateEnd
      ? formatBucketRangeLabel(row.dateStart, row.dateEnd, row.bucketSize ?? 1)
      : label
        ? shortDate(label)
        : '');

  const formatValue = (v: number) =>
    isAggregated ? v.toLocaleString(undefined, { maximumFractionDigits: 1 }) : v.toLocaleString();

  return (
    <div style={tooltipContainerStyle()} className="min-w-[160px] p-2.5 space-y-1.5">
      <p className="text-[11px] font-medium text-muted-foreground">{heading}</p>
      <div className="border-t border-border/50 pt-1.5 space-y-1">
        {[...payload].reverse().map(p => {
          const u = userMap.get(p.dataKey);
          if (!u || p.value === 0) return null;
          return (
            <div key={p.dataKey} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className="flex-shrink-0 w-2 h-2 rounded-full"
                  style={{ background: colorMap.get(p.dataKey) ?? '#888' }}
                />
                <span className="truncate text-[11px] text-foreground">{displayName(u)}</span>
              </div>
              <span className="text-[11px] font-medium tabular-nums text-foreground flex-shrink-0">
                {formatValue(p.value ?? 0)}
              </span>
            </div>
          );
        })}
      </div>
      {payload.length > 1 && (
        <div className="border-t border-border/50 pt-1 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Total</span>
          <span className="text-[11px] font-semibold tabular-nums text-foreground">{formatValue(total)}</span>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const OrgUsageChart = () => {
  const [metric, setMetric] = React.useState<MetricKey>('messages');
  const [rangeKey, setRangeKey] = React.useState<RangeKey>('30d');
  const [periodEnd, setPeriodEnd] = React.useState(todayKey);
  const [hiddenUsers, setHiddenUsers] = React.useState<Set<string>>(new Set());

  const selectedRange = RANGE_OPTIONS.find(r => r.key === rangeKey) ?? RANGE_OPTIONS[1];
  const { windowDays, stepDays } = selectedRange;

  const today = todayKey();
  const isCurrentPeriod = periodEnd >= today;
  // Let the server pick "today" for the live window — avoids client/server UTC skew
  // that was clearing series via a strict end-date match.
  const apiEndDate = isCurrentPeriod ? undefined : periodEnd;

  const { data, isPending, isFetching, isError } = useAdminUsageTimeseriesQuery(
    windowDays,
    apiEndDate
  );
  const { data: teamData } = useAdminTeamMembersQuery(true);

  const adminUserIds = React.useMemo(() => {
    const members = teamData?.status === 200 ? teamData.body.members : [];
    return new Set(
      members.filter(m => m.role === 'admin' && m.status === 'active').map(m => m.userId)
    );
  }, [teamData]);

  const body = data?.status === 200 ? data.body : undefined;
  const users = body?.users ?? [];
  const apiSeries = body?.series ?? [];

  const rangeEnd = body?.range?.end ?? (isCurrentPeriod ? today : periodEnd);
  const rangeStart =
    body?.range?.start ?? shiftDateKey(rangeEnd, -(windowDays - 1));
  const periodLabel = formatPeriodLabel(rangeStart, rangeEnd);

  const series = React.useMemo(() => {
    if (apiSeries.length > 0) {
      return apiSeries.length === windowDays ? apiSeries : apiSeries.slice(-windowDays);
    }
    return buildDateRange(rangeEnd, windowDays).map(date => ({
      date,
      totals: { messages: 0, sessions: 0, files: 0 },
      perUser: users.map(u => ({
        userId: u.userId,
        messages: 0,
        sessions: 0,
        files: 0,
      })),
    }));
  }, [apiSeries, rangeEnd, windowDays, users]);

  const handleRangeChange = (nextKey: RangeKey) => {
    setRangeKey(nextKey);
    setPeriodEnd(todayKey());
  };

  const shiftPeriod = (direction: -1 | 1) => {
    setPeriodEnd(prev => {
      const anchor = prev >= today ? today : prev;
      const next = shiftDateKey(anchor, direction * stepDays);
      return next > today ? today : next;
    });
  };

  // Stable color assignment by index in the returned users array
  const colorMap = React.useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u, i) => map.set(u.userId, getUserColor(i)));
    return map;
  }, [users]);

  const visibleUsers = users.filter(u => !hiddenUsers.has(u.userId));

  const isAggregated = rangeKey !== '7d';

  const chartSeries = React.useMemo(
    () => seriesForChartRange(series, rangeKey),
    [series, rangeKey]
  );

  // Pivot series data into recharts-friendly flat objects: { date, [userId]: value, ... }
  const chartData = React.useMemo(
    () =>
      chartSeries.map(day => {
        const row: Record<string, string | number> = {
          date: day.date,
          dateStart: day.dateStart,
          dateEnd: day.dateEnd,
          bucketSize: day.bucketSize,
          dateLabel: formatBucketRangeLabel(day.dateStart, day.dateEnd, day.bucketSize),
        };
        for (const u of day.perUser) {
          row[u.userId] = hiddenUsers.has(u.userId) ? 0 : u[metric];
        }
        return row;
      }),
    [chartSeries, metric, hiddenUsers]
  );

  const toggleUser = (userId: string) => {
    setHiddenUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const peakInsight = React.useMemo(() => {
    let best: { date: string; value: number; label: string } | null = null;
    for (const row of chartData) {
      const total = visibleUsers.reduce(
        (sum, u) => sum + (Number(row[u.userId]) || 0),
        0
      );
      if (!best || total > best.value) {
        best = {
          date: String(row.date),
          value: total,
          label: String(row.dateLabel ?? shortDate(String(row.date))),
        };
      }
    }
    return best && best.value > 0 ? best : null;
  }, [chartData, visibleUsers]);

  const colors = getChartColors();

  if (isPending) return <ChartSkeleton />;

  if (isError) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-muted-foreground/60 rounded-xl border border-border/40 bg-bg-neutral-8">
        Could not load usage data.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Metric tabs */}
      <div className="flex gap-1 border-b border-border">
        {METRIC_OPTIONS.map(opt => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setMetric(opt.key)}
            aria-pressed={metric === opt.key}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
              metric === opt.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        ))}
      </div>

      {/* Range toggle + period navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => handleRangeChange(opt.key)}
              className={cn(
                'px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                rangeKey === opt.key
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => shiftPeriod(-1)}
            title="Previous period"
            className={cn(
              'p-1.5 rounded-lg text-muted-foreground transition-colors',
              'hover:text-foreground hover:bg-muted/60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30'
            )}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="min-w-[140px] text-center text-xs font-medium text-foreground tabular-nums">
            {periodLabel}
          </span>
          <button
            type="button"
            onClick={() => shiftPeriod(1)}
            disabled={isCurrentPeriod}
            title="Next period"
            className={cn(
              'p-1.5 rounded-lg text-muted-foreground transition-colors',
              'hover:text-foreground hover:bg-muted/60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
              'disabled:opacity-30 disabled:pointer-events-none'
            )}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Chart — always rendered; uses API series or a zero-filled date scaffold */}
      <div
        className={cn(
          'relative rounded-xl border border-border/50 bg-bg-neutral-8 p-3 transition-opacity',
          isFetching && 'opacity-60'
        )}
      >
        {peakInsight && (
          <div
            className="absolute top-2 right-2 z-10 flex items-center gap-1.5 max-w-[min(100%-1rem,220px)] px-2 py-1 rounded-lg border border-primary/25 bg-primary/10 text-[10px] text-primary shadow-sm"
            title={`Highest ${metric} in this period`}
          >
            <TrendingUp size={11} className="flex-shrink-0" />
            <span className="truncate">
              Peak{' '}
              <span className="font-semibold tabular-nums">
                {isAggregated
                  ? peakInsight.value.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : peakInsight.value.toLocaleString()}
              </span>
              {' · '}
              {peakInsight.label}
            </span>
          </div>
        )}
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <defs>
              {visibleUsers.map(u => {
                const color = colorMap.get(u.userId) ?? '#888';
                return (
                  <linearGradient key={u.userId} id={`grad-${u.userId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid
              {...cartesianGridProps}
              stroke={colors.grid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={shortDate}
              tick={{ fontSize: 10, fill: colors.axis }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: colors.axis }}
              tickLine={false}
              axisLine={false}
              allowDecimals={isAggregated}
            />
            <Tooltip
              content={
                <CustomTooltip
                  users={users}
                  colorMap={colorMap}
                  metric={metric}
                  isAggregated={isAggregated}
                />
              }
              cursor={{ stroke: colors.grid, strokeWidth: 1 }}
            />
            {visibleUsers.map(u => {
              const color = colorMap.get(u.userId) ?? '#888';
              return (
                <Area
                  key={u.userId}
                  type="monotone"
                  dataKey={u.userId}
                  stroke={color}
                  strokeWidth={1.5}
                  fill={`url(#grad-${u.userId})`}
                  dot={false}
                  activeDot={{ r: 3, strokeWidth: 0 }}
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Per-user legend with toggle */}
      {users.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {users.map(u => {
            const color = colorMap.get(u.userId) ?? '#888';
            const hidden = hiddenUsers.has(u.userId);
            return (
              <button
                key={u.userId}
                type="button"
                onClick={() => toggleUser(u.userId)}
                title={u.email}
                className={cn(
                  'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium',
                  'border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                  hidden
                    ? 'border-border/40 text-muted-foreground/40 bg-transparent'
                    : 'border-border/60 text-foreground bg-muted/30'
                )}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0 transition-opacity"
                  style={{ background: color, opacity: hidden ? 0.3 : 1 }}
                />
                <span className="inline-flex items-center gap-1 min-w-0 max-w-[160px]">
                  <span className="truncate">{displayName(u)}</span>
                  {adminUserIds.has(u.userId) && (
                    <span className="flex-shrink-0 text-muted-foreground font-normal">(admin)</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

OrgUsageChart.displayName = 'OrgUsageChart';
